import './template-styles/ArticlePage.scss'

import React from 'react'
import { useLocation } from 'react-router-dom'
import { dynamicGa4DataLayerPush } from '@which/shared'

import { useFeatureIsOn, useFeatureValue } from '@growthbook/growthbook-react'

import type { Author, ImageWithSources, ReviewsAdviceMultiQuery } from '../../generated/frontend'
import { useReviewsAdviceMultiQuery } from '../../generated/frontend'
import { useBackStack } from '../../shared/BrowserBackStackProvider'
import {
  AffiliateDisclaimer,
  AssociatedArticles,
  BackToTop,
  Breadcrumb,
  ErrorComponent,
  HideOnArticleScroll,
  Loader,
  LocalFooter,
  PageError,
  PromoBlock,
  Redirect,
  ResubscribeBanner,
  RetirementStatusSelector,
  SharingOptions,
  ToolCTAWithHeroImage,
} from '../../shared/components'
import { FreeAccountWelcomeBanner } from '../../shared/components/FreeAccountWelcomeBanner'
import { ModelRecommendations } from '../../shared/components/ModelRecommendations'
import { RecentlyViewedBanner } from '../../shared/components/RecentlyViewedBanner/RecentlyViewedBanner'
import { Layout, Model } from '../../shared/components/Recommendations'
import { getDataLayerScripts } from '../../shared/data-layer'
import { getDFPScripts } from '../../shared/dfp'
import { GridArticle, GridItemArticle } from '../../shared/grids'
import { useBaseMetaTags } from '../../shared/hooks/useBaseMetaTags'
import { useFullUrl } from '../../shared/hooks/useFullUrl'
import { articleBodyRenderer } from '../../shared/renderers/article-body-renderer'
import type { PageInfo } from '../../shared/types/PageInfo'
import { usePageProps } from '../../shared/usePageProps'
import { getPreviousUrl } from '../../shared/utils/getPreviousUrl/getPreviousUrl'
import { removeFalsy } from '../../shared/utils/remove-falsy'
import { PageWrapperWithMeta } from '../../shared/wrappers/PageWrapperWithMeta'
import { ArticleHero } from '../article/components/ArticleHero/ArticleHero'
import { AuthorWrapper } from '../article/components/Author/AuthorWrapper'
import { Header } from '../article/components/Header/Header'
import { IntroParagraph } from '../article/components/IntroParagraph/IntroParagraph'
import { WhichConnectMessage } from '../article/components/WhichConnectMessage/WhichConnectMessage'
import { EditableCTAPanel } from '../home/components/EditableCTAPanel/EditableCTAPanel'
import { ToastNotification } from '../reviews/shared/components/ToastNotification'
import { ArticleTemplateContext } from './ArticleTemplateContext'
import { ArticleMemberBenefits } from './components/ArticleMemberBenefits'
import { CompareTrayWrapper } from './components/CompareTrayWrapper/CompareTrayWrapper'
import { ArticlePageBodyGam } from './components/Gam/ArticlePageBodyGam'
import { ArticlePageRhsGam } from './components/Gam/ArticlePageRhsGam'
import { JumpLinks } from './components/JumpLinks/JumpLinks'
import type { SquirrelScriptsData } from './hooks/use-squirrel-script'
import { useSquirrelScript } from './hooks/use-squirrel-script'
import { UserFeedbackIframe } from './page-snippets/user-feedback-iframe'
import { baseDataLayer } from './utils/base-data-layer'
import { dynamicTableDatalayer } from './utils/dynamic-table-datalayer'
import { findMortgageRatesData } from './utils/find-mortgage-rates-data'
import { renderResubscribeBanner } from './utils/render-resubscribe-banner'
import { generateSchema } from './utils/schema'
import { useFreeAccountState } from './utils/useFreeAccountState'

const ReviewsAdviceMultiPage: React.FC = () => {
  const { pathname, search } = useLocation()
  const { context = '' } = usePageProps()
  const { displayLocking } = useFreeAccountState()

  const { loading, error, data } = useReviewsAdviceMultiQuery({
    variables: { url: `${pathname}${search}`, context },
  })

  const { getFullUrl } = useFullUrl()
  const backStack = useBackStack()
  const previousUrl = getPreviousUrl({ backStack })

  const { getBaseMetaTags } = useBaseMetaTags()

  useSquirrelScript(data as SquirrelScriptsData)

  const resubscribeBannerFlag = useFeatureIsOn('resubscribe-banner')
  const showPppOnArticles = useFeatureIsOn('DSP-816-ppp-on-articles')
  const showFreeAccountLocking = useFeatureIsOn('acq-651-homepage-free-account')
  const gbTestValue = useFeatureValue('ctx-011-contextualised-newsletter', '')
  const { userAccessState } = usePageProps()

  const transformTypeDecision = userAccessState?.transformTypeDecision ?? ''
  const isPaidMember = ['AUTHENTICATED_FULL_ACCESS', 'FULL_ACCESS'].includes(transformTypeDecision)

  if (loading) {
    return <Loader />
  }

  if (error) {
    return <ErrorComponent error={error} />
  }

  if (!data?.articlePage) {
    return <PageError pageName="Article Page" />
  }

  const { articlePage } = data

  if (articlePage.__typename === 'Redirect') {
    return <Redirect to={articlePage.redirectUrl} />
  }

  const {
    articleBodyTemplate,
    articleConfig,
    authors,
    breadcrumb,
    header,
    heroImage,
    introParagraph,
    jumpLinks,
    meta,
    multiPageGuide,
    optionalArticleData,
    sharingData,
    showResubBanner,
    heroCTA,
  } = articlePage

  const articleFreeAccountStatus = articleConfig.freeAccountStatus
  const shouldShowFreeAccountLock =
    showFreeAccountLocking && articleFreeAccountStatus === 'lock' && displayLocking
  const baseMetaTags = getBaseMetaTags({
    meta,
    context,
    imageUrl: heroImage?.src,
    twitterCard: 'summary_large_image',
  })
  const metaTags = getMetaTags({
    articlePage,
    baseMetaTags,
    urls: { currentUrl: getFullUrl(), previousUrl },
    gbTestValue,
  })

  const gamPageInfo = {
    vertical: meta.taxonomyHierarchy.vertical?.slug,
    sub_vertical: meta.taxonomyHierarchy.subVertical?.slug,
    category: meta.taxonomyHierarchy.category?.slug,
    pageType: 'mpg-article',
    tags: meta.keywords,
    content_id: meta.contentId,
  }

  const moneyPromoBlock = optionalArticleData?.components?.moneyPromoBlock
  const consumerRightsAndMoneyLocalFooter = optionalArticleData?.components?.localFooter

  const handleShowMoreOnJumplinkClick = () => {
    const showMoreBtn = document.querySelector<HTMLButtonElement>(
      '[data-testid="show-more-button"]'
    )

    if (showMoreBtn) {
      showMoreBtn.click()
    }
  }

  const promoBlockTracking = (position: string) => {
    return {
      onClick: () => {
        dynamicGa4DataLayerPush({
          event: 'click_cta',
          utagid: 'WHC873DP01',
          item_group: 'in article cta',
          item_text: moneyPromoBlock?.ctaLabel,
          item_parent_text: moneyPromoBlock?.title,
          item_spec: position,
        })
      },
    }
  }

  const isPensionArticle = meta?.taxonomyHierarchy?.subVertical?.slug === 'pensions-and-retirement'

  const showToolCTAWithHero = Boolean(
    heroCTA && heroCTA.title && heroCTA.button_url && heroCTA.button_text
  )

  return (
    <ArticleTemplateContext.Provider
      value={{
        meta,
      }}
    >
      <div
        ref={domUpdates}
        data-testid="template-portal"
        data-template-type="reviews-advice-multi-article"
      >
        <RecentlyViewedBanner />
        <FreeAccountWelcomeBanner />
        {renderResubscribeBanner(resubscribeBannerFlag, transformTypeDecision) && (
          <ResubscribeBanner showBanner={showResubBanner} />
        )}
        <Breadcrumb {...breadcrumb} />
        <PageWrapperWithMeta metaTags={metaTags}>
          <GridArticle>
            <GridItemArticle area="header">
              <AffiliateDisclaimer affiliateDisclaimer={articleConfig?.affiliateDisclaimer} />
              <Header {...header} />
            </GridItemArticle>
            <GridItemArticle area="author">
              <AuthorWrapper authors={authors} withAuthorLink={true} />
            </GridItemArticle>
            <GridItemArticle area="hero">
              <>
                <ArticleHero
                  heroImage={heroImage as ImageWithSources}
                  compact={showToolCTAWithHero}
                />
                <ToolCTAWithHeroImage {...heroCTA} />
              </>
            </GridItemArticle>
            <GridItemArticle area="jumplinks">
              <JumpLinks handleOnClick={handleShowMoreOnJumplinkClick} links={jumpLinks} />
            </GridItemArticle>
            <GridItemArticle area="share" className="gridarea-share">
              <HideOnArticleScroll>
                <SharingOptions sharingData={sharingData} />
              </HideOnArticleScroll>
            </GridItemArticle>
            <GridItemArticle
              area="body"
              className={`gridarea-body ${articleConfig?.freeAccountStatus === 'lock' ? 'paywall' : ''} ${shouldShowFreeAccountLock ? 'free-locked-article-body-multi' : ''}`}
            >
              <IntroParagraph text={introParagraph} />
              {articleBodyRenderer(articleBodyTemplate, '', {
                articleUrl: getFullUrl(),
                showFreeAccountLock: shouldShowFreeAccountLock,
                userLoggedIn: userAccessState?.userLoggedIn,
              })}
              <WhichConnectMessage showMessage={articleConfig?.whichConnectMessage} />
              <PromoBlock {...moneyPromoBlock} {...promoBlockTracking('bottom of page')} />
              <div className="related-footer">
                {isPensionArticle && (
                  <RetirementStatusSelector
                    className="retirement-status-selector"
                    position="bottom"
                  ></RetirementStatusSelector>
                )}
                <AssociatedArticles
                  componentType="multi-page-guide"
                  position="bottom"
                  className="associated-articles"
                  useShowMoreComponent={true}
                  {...multiPageGuide}
                />
                <div className="associated-articles">
                  <ModelRecommendations
                    modelKey={Model.RECENT_CATEGORY}
                    position="bottom"
                    whichId="related links"
                    layout={Layout.TextLinks}
                  />
                </div>
              </div>
              <UserFeedbackIframe />
              {showPppOnArticles ? (
                <EditableCTAPanel />
              ) : (
                <ArticlePageBodyGam pageInfo={gamPageInfo} />
              )}
              <SharingOptions sharingData={sharingData} orientation="vertical" />
              <div className="back-to-top-wrapper">
                <BackToTop />
              </div>
            </GridItemArticle>
            <GridItemArticle area="memberbenefits">
              <ArticleMemberBenefits />
            </GridItemArticle>
            <GridItemArticle area="related" className="gridarea-related">
              <aside className="sidebar">
                <PromoBlock {...moneyPromoBlock} {...promoBlockTracking('sidebar')} />
                {isPensionArticle && (
                  <RetirementStatusSelector
                    className="retirement-status-selector"
                    position="sidebar"
                  ></RetirementStatusSelector>
                )}
                <AssociatedArticles
                  componentType="multi-page-guide"
                  position="right"
                  useShowMoreComponent={true}
                  {...multiPageGuide}
                />
                <ModelRecommendations
                  modelKey={Model.RECENT_CATEGORY}
                  position="right"
                  whichId="related links"
                  layout={Layout.TextLinks}
                />
                {showPppOnArticles ? (
                  <EditableCTAPanel hideImageWhenNarrow={true} />
                ) : (
                  <ArticlePageRhsGam pageInfo={gamPageInfo} />
                )}
              </aside>
            </GridItemArticle>
            {consumerRightsAndMoneyLocalFooter && (
              <GridItemArticle area="footer">
                <LocalFooter data={consumerRightsAndMoneyLocalFooter} />
              </GridItemArticle>
            )}
          </GridArticle>
        </PageWrapperWithMeta>
      </div>
      {isPaidMember && (
        <CompareTrayWrapper taxonomySlug={meta?.taxonomyHierarchy?.category?.slug} />
      )}
      <ToastNotification />
    </ArticleTemplateContext.Provider>
  )
}

export default ReviewsAdviceMultiPage

const domUpdates = () => {
  dynamicTableDatalayer()
}

export type ReviewsAdviceMultiArticleData = ReviewsAdviceMultiQuery['articlePage'] & {
  __typename: 'ArticlePage'
}

const getMetaTags = ({
  articlePage: { meta, articleBodyTemplate, heroImage, authors, articleConfig },
  baseMetaTags,
  urls,
  gbTestValue,
}: {
  articlePage: ReviewsAdviceMultiArticleData
  baseMetaTags: JSX.Element[]
  urls: { currentUrl: string; previousUrl?: string }
  gbTestValue: string
}) => {
  const authorsMeta = authors.length
    ? [<meta key="author" name="author" content={authors.map(({ name }) => name).join(', ')} />]
    : []

  const keywords = meta.keywords.length
    ? [<meta key="keywords" name="keywords" content={meta.keywords.join(', ')} />]
    : []

  const basePageInfo: PageInfo = {
    pageType: 'mpg-article',
    vertical: meta.taxonomyHierarchy?.vertical.slug,
    sub_vertical: meta.taxonomyHierarchy?.subVertical?.slug,
    super_category: meta.taxonomyHierarchy?.superCategory?.slug,
    category: meta.taxonomyHierarchy?.category?.slug,
    content_id: meta.contentId,
    isVideoArticle: false,
  }

  const mortgageRatesLastUpdated = findMortgageRatesData(articleBodyTemplate)

  // data layer
  const dataLayerPageInfo: PageInfo = {
    ...basePageInfo,
    ...baseDataLayer(meta, urls, {
      articleBodyTemplate,
      extraPageComponents: articleConfig.freeAccountStatus === 'lock' ? ['fa-locked'] : [],
    }),
    keywords: meta.keywords?.length ? meta.keywords.join(' | ') : '',
    tags: meta.keywords?.length ? meta.keywords.join(' | ') : '',
    author: authors.length ? authors.map(({ name }) => name).join(' | ') : '',
    articleType: articleConfig.articleType,
    content_type: 'advice',
  }
  const dataLayer = getDataLayerScripts([...(meta.dataLayer || []), removeFalsy(dataLayerPageInfo)])

  // doubleclick for publishers
  const dfpMeta = getDFPScripts(meta.dataLayer, {
    ...basePageInfo,
    keywords: meta.keywords,
    tags: meta.keywords,
    GB_test_value: gbTestValue,
  })

  // schema for SEO
  const schema = generateSchema({
    meta,
    authors: authors as Author[],
    schemaImages: heroImage?.schemaImages,
    mortgageRatesLastUpdated,
    isPaywalledArticle: articleConfig?.freeAccountStatus === 'lock',
    paywallCssSelector: '.paywall',
  })

  return baseMetaTags.concat(
    authorsMeta,
    keywords,
    dataLayer,
    dfpMeta,
    schema,
    <link rel="preconnect" href="https://securepubads.g.doubleclick.net" />
  )
}
